'use client';

import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useDesign } from '@/context/DesignContext';

interface TestimonialsParapharmaProps {
  title?: string;
  subtitle?: string;
  testimonials?: Array<{
    name: string;
    role: string;
    text: string;
    rating: number;
  }>;
  design?: any;
}

export function TestimonialsParapharma({
  title = 'Ils nous font confiance',
  subtitle = 'Découvrez ce que nos clients disent de nous',
  testimonials = [
    { name: 'Sophie Martin', role: 'Cliente fidèle', text: "Des produits d'exception et un service irréprochable. Je ne fais confiance qu'à eux pour ma santé.", rating: 5 },
    { name: 'Dr. Thomas Bernard', role: 'Médecin généraliste', text: 'Je recommande souvent cette parapharmacie à mes patients. La qualité est toujours au rendez-vous.', rating: 5 },
    { name: 'Marie Dubois', role: 'Maman de 3 enfants', text: "Enfin une parapharmacie où je trouve tout ce dont ma famille a besoin, avec des conseils avisés.", rating: 5 }
  ],
  design: propDesign
}: TestimonialsParapharmaProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" as const } }
  };

  const sectionBg = design?.componentDesignMap?.TestimonialsParapharma?.sectionBackground || 'linear-gradient(135deg, #0F172A, #1E293B)';
  const titleColor = design?.componentDesignMap?.TestimonialsParapharma?.titleColor || '#FFFFFF';
  const subtitleColor = design?.componentDesignMap?.TestimonialsParapharma?.subtitleColor || '#94A3B8';
  const cardBg = design?.componentDesignMap?.TestimonialsParapharma?.cardBackground || 'rgba(255,255,255,0.1)';
  const cardBorder = design?.componentDesignMap?.TestimonialsParapharma?.cardBorderColor || 'rgba(255,255,255,0.1)';
  const starColor = design?.componentDesignMap?.TestimonialsParapharma?.starColor || design?.colors?.accent || '#F59E0B';
  const quoteColor = design?.componentDesignMap?.TestimonialsParapharma?.quoteColor || 'rgba(255,255,255,0.9)';
  const authorNameColor = design?.componentDesignMap?.TestimonialsParapharma?.authorNameColor || '#FFFFFF';
  const authorRoleColor = design?.componentDesignMap?.TestimonialsParapharma?.authorRoleColor || '#94A3B8';
  const avatarGradient = design?.componentDesignMap?.TestimonialsParapharma?.avatarGradient || 'from-blue-500 to-purple-500';

  return (
    <section className="py-20 lg:py-32 text-white" style={{ background: sectionBg }}>
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: titleColor }}>{title}</h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: subtitleColor }}>{subtitle}</p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="backdrop-blur-sm rounded-2xl p-8 border"
              style={{ background: cardBg, borderColor: cardBorder }}
            >
              <div className="flex gap-1 mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" style={{ color: starColor }} />
                ))}
              </div>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: quoteColor }}>"{testimonial.text}"</p>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${avatarGradient} rounded-full flex items-center justify-center font-bold`}>
                  {testimonial.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-semibold" style={{ color: authorNameColor }}>{testimonial.name}</p>
                  <p className="text-sm" style={{ color: authorRoleColor }}>{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}